import type { EmptyObject, OverrideProperties, SimplifyDeep } from 'type-fest'
import { z } from 'zod'

import {
  type ActivityDomainSelectModel,
  ActivityDomainSelectModelSchema,
  type ActivityType,
} from './activity.models.ts'
import {
  type AdvancePaymentDomainSelectModel,
  AdvancePaymentDomainSelectModelSchema,
} from './advance-payment.models.ts'
import {
  type AgreementDomainSelectModel,
  AgreementDomainSelectModelSchema,
} from './agreement.models.ts'
import {
  type BidDomainSelectModel,
  BidDomainSelectModelSchema,
} from './bid.models.ts'
import {
  type BillingProfileDomainSelectModel,
  BillingProfileDomainSelectModelSchema,
} from './billing-profile.models.ts'
import {
  type DeliverableDomainSelectModel,
  DeliverableDomainSelectModelSchema,
} from './deliverable.models.ts'
import {
  type DocumentDomainSelectModel,
  DocumentDomainSelectModelSchema,
} from './document.models.ts'
import {
  type FileDomainSelectModel,
  FileDomainSelectModelSchema,
} from './file.models.ts'
import {
  type InvoiceDomainSelectModel,
  InvoiceDomainSelectModelSchema,
} from './invoice.models.ts'
import {
  type InvoiceLineItemDomainSelectModel,
  InvoiceLineItemDomainSelectModelSchema,
} from './invoice-lineitem.models.ts'
import {
  type LocationDomainSelectModel,
  LocationDomainSelectModelSchema,
} from './location.models.ts'
import {
  type NotificationDomainSelectModel,
  NotificationDomainSelectModelSchema,
} from './notification.models.ts'
import {
  NewProjectNotificationPreferenceSchema,
  type NotificationPreferenceDomainSelectModel,
} from './notification-preference.models.ts'
import {
  type OrganizationDomainSelectModel,
  OrganizationDomainSelectModelSchema,
  type OrganizationType,
} from './organization.models.ts'
import {
  type PackageDomainSelectModel,
  PackageDomainSelectModelSchema,
} from './package.models.ts'
import {
  type PackageServiceDomainSelectModel,
  PackageServiceDomainSelectModelSchema,
} from './package-service.models.ts'
import {
  type PartnerProgramDomainSelectModel,
  PartnerProgramDomainSelectModelSchema,
} from './partner-program.models.ts'
import {
  type ProjectViewDomainSelectModel,
  ProjectViewDomainSelectModelSchema,
} from './project.models.ts'
import {
  type ProjectServiceDomainSelectModel,
  ProjectServiceDomainSelectModelSchema,
} from './project-service.models.ts'
import {
  type ProofDomainSelectModel,
  ProofDomainSelectModelSchema,
} from './proof.models.ts'
import {
  type ReviewDomainSelectModel,
  ReviewDomainSelectModelSchema,
} from './review.models.ts'
import {
  type RoundDomainSelectModel,
  RoundDomainSelectModelSchema,
} from './round.models.ts'
import {
  type ServiceDomainSelectModel,
  ServiceDomainSelectModelSchema,
} from './service.models.ts'
import {
  type SurveyDomainSelectModel,
  SurveyDomainSelectModelSchema,
} from './survey.models.ts'
import {
  type TagDomainSelectModel,
  TagDomainSelectModelSchema,
} from './tag.models.ts'
import {
  type TeamDomainSelectModel,
  TeamDomainSelectModelSchema,
} from './team.models.ts'
import {
  type TeamProfileDomainSelectModel,
  TeamProfileDomainSelectModelSchema,
} from './team-profile.models.ts'
import type { TeamId } from './unique-id.models.ts'
import {
  type UserDomainSelectModel,
  UserDomainSelectModelSchema,
} from './user.models.ts'

/**
 * Typescript does not allow circular references between objects so the validation can only
 * be done one level deep which is sufficient for testing and mapping purposes. The relations
 * are defined using .passthrough() so that the validation won't fail when there are nested
 * relations. The following types/functions are used to define the relations for consistency.
 */

/**
 * A helper function to define the relations for a specific query model
 * @param relations The set of relations for a specific query model
 * @returns A schema that does not allow any additional properties and allows the relations to be optional
 */
type Relations<Links> = Partial<Links>

const relations = <Links extends z.ZodRawShape>(links: Links) =>
  z.object(links).partial().shape

/**
 * Helper to safely cast a Zod schema to a specific type
 */
const asZodType = <T>(schema: z.ZodType) => schema as z.ZodType<T>

/**
 * A helper function to define a one-to-one relation
 * @param schema The referenced underlying select model schema
 * @returns A schema with .passthrough() applied (so that nested relations won't fail validation)
 *  refined to coerce the parsed value to the specified query model type
 */
type One<To> = To

const one = <QueryModel>(schema: z.ZodObject<z.ZodRawShape>) => {
  return asZodType<One<QueryModel>>(z.looseObject(schema.shape))
}

/**
 * A helper function to define a one-to-many relation
 * @param schema The referenced underlying select model schema
 * @returns A schema with .passthrough() applied (so that nested relations won't fail validation)
 * refined to coerce the parsed array elements to the specified query model type
 */
type Many<To> = Array<To>

const many = <QueryModel>(schema: z.ZodObject<z.ZodRawShape>) => {
  return asZodType<Many<QueryModel>>(z.looseObject(schema.shape).array())
}

/**
 * ActivityDomainQueryModel
 */
export const ActivityDomainQueryModelSchema =
  ActivityDomainSelectModelSchema.extend(
    relations({
      actor: one<UserDomainQueryModel>(UserDomainSelectModelSchema),
    })
  )

export type ActivityDomainQueryModel<Type extends ActivityType = ActivityType> =
  ActivityDomainSelectModel<Type> &
    Relations<{
      actor: One<UserDomainQueryModel>
    }>

/**
 * AdvancePaymentDomainQueryModel
 */
export const AdvancePaymentDomainQueryModelSchema =
  AdvancePaymentDomainSelectModelSchema.extend({
    request: AdvancePaymentDomainSelectModelSchema.shape.request.extend(
      relations({
        actor: one<UserDomainQueryModel>(UserDomainSelectModelSchema),
      })
    ),
    review: AdvancePaymentDomainSelectModelSchema.shape.review
      .unwrap()
      .extend(
        relations({
          actor: one<UserDomainQueryModel>(UserDomainSelectModelSchema),
        })
      )
      .optional(),
  }).extend(
    relations({
      invoice: one<InvoiceDomainQueryModel>(InvoiceDomainSelectModelSchema),
      project: one<ProjectDomainQueryModel>(ProjectViewDomainSelectModelSchema),
    })
  )

export type AdvancePaymentDomainQueryModel = OverrideProperties<
  AdvancePaymentDomainSelectModel &
    Relations<{
      project: One<ProjectDomainQueryModel>
      invoice: One<InvoiceDomainQueryModel>
    }>,
  {
    request: AdvancePaymentDomainSelectModel['request'] &
      Relations<{
        actor: One<UserDomainQueryModel>
      }>
    review?: AdvancePaymentDomainSelectModel['review'] &
      Relations<{
        actor: One<UserDomainQueryModel>
      }>
  }
>

/**
 * AgreementDomainQueryModel
 */
export const AgreementDomainQueryModelSchema =
  AgreementDomainSelectModelSchema.extend(relations({}))

export type AgreementDomainQueryModel = AgreementDomainSelectModel &
  Relations<EmptyObject>

/**
 * BidDomainQueryModel
 */
export const BidDomainQueryModelSchema = BidDomainSelectModelSchema.extend(
  relations({
    agencyTeam: one<TeamDomainQueryModel>(TeamDomainSelectModelSchema),
    documents: many<DocumentDomainQueryModel>(DocumentDomainSelectModelSchema),
    files: many<FileDomainQueryModel>(FileDomainSelectModelSchema),
    project: one<ProjectDomainQueryModel>(ProjectViewDomainSelectModelSchema),
  })
)

export type BidDomainQueryModel = BidDomainSelectModel &
  Relations<{
    agencyTeam: One<TeamDomainQueryModel>
    documents: Many<DocumentDomainQueryModel>
    files: Many<FileDomainQueryModel>
    project: One<ProjectDomainQueryModel>
  }>

/**
 * BillingProfileDomainQueryModel
 */
export const BillingProfileDomainQueryModelSchema =
  BillingProfileDomainSelectModelSchema.extend(
    relations({
      organization: one<OrganizationDomainQueryModel>(
        OrganizationDomainSelectModelSchema
      ),
      teams: many<TeamDomainQueryModel>(TeamDomainSelectModelSchema),
    })
  )

export type BillingProfileDomainQueryModel = BillingProfileDomainSelectModel &
  Relations<{
    organization: One<OrganizationDomainQueryModel>
    teams: Many<TeamDomainQueryModel>
  }>

/**
 * DeliverableDomainQueryModel
 */
export const DeliverableDomainQueryModelSchema =
  DeliverableDomainSelectModelSchema.extend(
    relations({
      file: one<FileDomainQueryModel>(FileDomainSelectModelSchema),
      project: one<ProjectDomainQueryModel>(ProjectViewDomainSelectModelSchema),
      service: one<ProjectServiceDomainQueryModel>(
        ProjectServiceDomainSelectModelSchema
      ),
    })
  )

export type DeliverableDomainQueryModel = DeliverableDomainSelectModel &
  Relations<{
    file: One<FileDomainQueryModel>
    project: One<ProjectDomainQueryModel>
    service: One<ProjectServiceDomainQueryModel>
  }>

/**
 * DocumentDomainQueryModel
 */
export const DocumentDomainQueryModelSchema =
  DocumentDomainSelectModelSchema.extend(
    relations({
      bid: one<BidDomainQueryModel>(BidDomainSelectModelSchema),
      project: one<ProjectDomainQueryModel>(ProjectViewDomainSelectModelSchema),
    })
  )

export type DocumentDomainQueryModel = DocumentDomainSelectModel &
  Relations<{
    bid: One<BidDomainQueryModel>
    project: One<ProjectDomainQueryModel>
  }>

/**
 * FileDomainQueryModel
 */
export const FileDomainQueryModelSchema = FileDomainSelectModelSchema.extend(
  relations({
    owner: one<UserDomainQueryModel>(UserDomainSelectModelSchema),
    project: one<ProjectDomainQueryModel>(ProjectViewDomainSelectModelSchema),
    service: one<ProjectServiceDomainQueryModel>(
      ProjectServiceDomainSelectModelSchema
    ),
    tags: many<TagDomainQueryModel>(TagDomainSelectModelSchema),
  })
)

export type FileDomainQueryModel = FileDomainSelectModel &
  Relations<{
    owner: One<UserDomainQueryModel>
    project: One<ProjectDomainQueryModel>
    service: One<ProjectServiceDomainQueryModel>
    tags: Many<TagDomainQueryModel>
  }>

/**
 * InvoiceDomainQueryModel
 */

export const InvoiceDomainQueryModelSchema =
  InvoiceDomainSelectModelSchema.extend(
    relations({
      project: one<ProjectDomainQueryModel>(ProjectViewDomainSelectModelSchema),
      lineitems: many<InvoiceLineItemDomainQueryModel>(
        InvoiceLineItemDomainSelectModelSchema
      ),
    })
  )

export type InvoiceDomainQueryModel = InvoiceDomainSelectModel &
  Relations<{
    project: One<ProjectDomainQueryModel>
    lineitems: Many<InvoiceLineItemDomainQueryModel>
  }>

/**
 * InvoiceLineItemDomainQueryModel
 */
export const InvoiceLineItemDomainQueryModelSchema =
  InvoiceLineItemDomainSelectModelSchema.extend(
    relations({
      invoice: one<InvoiceDomainQueryModel>(InvoiceDomainSelectModelSchema),
    })
  )

export type InvoiceLineItemDomainQueryModel = InvoiceLineItemDomainSelectModel &
  Relations<{
    invoice: One<InvoiceDomainQueryModel>
  }>

/**
 * LocationDomainQueryModel
 */
export const LocationDomainQueryModelSchema =
  LocationDomainSelectModelSchema.extend(relations({}))

export type LocationDomainQueryModel = LocationDomainSelectModel &
  Relations<EmptyObject>

/**
 * NotificationDomainQueryModel
 */
export const NotificationDomainQueryModelSchema =
  NotificationDomainSelectModelSchema.extend(
    relations({
      activity: one<ActivityDomainQueryModel>(ActivityDomainSelectModelSchema),
      recipient: one<UserDomainQueryModel>(UserDomainSelectModelSchema),
    })
  )

export type NotificationDomainQueryModel = NotificationDomainSelectModel &
  Relations<{
    activity: One<ActivityDomainQueryModel>
    recipient: One<UserDomainQueryModel>
  }>

/**
 * OrganizationDomainQueryModel
 */
export const OrganizationDomainQueryModelSchema =
  OrganizationDomainSelectModelSchema.extend(
    relations({
      billingProfiles: many<BillingProfileDomainQueryModel>(
        BillingProfileDomainSelectModelSchema
      ),
      partnerPrograms: many<PartnerProgramDomainQueryModel>(
        PartnerProgramDomainSelectModelSchema
      ),
      tags: many<TagDomainQueryModel>(TagDomainSelectModelSchema),
      teams: many<TeamDomainQueryModel>(TeamDomainSelectModelSchema),
      users: many<UserDomainQueryModel>(UserDomainSelectModelSchema),
    })
  )

export type OrganizationDomainQueryModel = OrganizationDomainSelectModel &
  Relations<{
    billingProfiles: Many<BillingProfileDomainQueryModel>
    partnerPrograms: Many<PartnerProgramDomainQueryModel>
    tags: Many<TagDomainQueryModel>
    teams: Many<TeamDomainQueryModel>
    users: Many<UserDomainQueryModel>
  }>

/**
 * PartnerProgramDomainQueryModel
 */
export const PartnerProgramDomainQueryModelSchema =
  PartnerProgramDomainSelectModelSchema.extend(
    relations({
      organization: one<OrganizationDomainQueryModel>(
        OrganizationDomainSelectModelSchema
      ),
    })
  )

export type PartnerProgramDomainQueryModel = PartnerProgramDomainSelectModel &
  Relations<{
    organization: One<OrganizationDomainQueryModel>
  }>

/**
 * PackageDomainQueryModel
 */
export const PackageDomainQueryModelSchema =
  PackageDomainSelectModelSchema.extend(
    relations({
      services: many<PackageServiceDomainQueryModel>(
        PackageServiceDomainSelectModelSchema
      ),
      tags: many<TagDomainQueryModel>(TagDomainSelectModelSchema),
    })
  )

export type PackageDomainQueryModel = PackageDomainSelectModel &
  Relations<{
    services: Many<PackageServiceDomainQueryModel>
    tags: Many<TagDomainQueryModel>
  }>

/**
 * PackageServiceDomainQueryModel
 */
export const PackageServiceDomainQueryModelSchema =
  PackageServiceDomainSelectModelSchema.extend(
    relations({
      tags: many<TagDomainQueryModel>(TagDomainSelectModelSchema),
    })
  )

export type PackageServiceDomainQueryModel = PackageServiceDomainSelectModel &
  Relations<{
    tags: Many<TagDomainQueryModel>
  }>

/**
 * ProjectDomainQueryModel
 */
export const ProjectDomainQueryModelSchema =
  ProjectViewDomainSelectModelSchema.extend(
    relations({
      acceptor: one<UserDomainQueryModel>(UserDomainSelectModelSchema),
      activity: many<ActivityDomainQueryModel>(ActivityDomainSelectModelSchema),
      advancePayments: many<AdvancePaymentDomainQueryModel>(
        AdvancePaymentDomainSelectModelSchema
      ),
      agencyTeam: one<TeamDomainQueryModel>(TeamDomainSelectModelSchema),
      bids: many<BidDomainQueryModel>(BidDomainSelectModelSchema),
      brandTeam: one<TeamDomainQueryModel>(TeamDomainSelectModelSchema),
      deliverables: many<DeliverableDomainQueryModel>(
        DeliverableDomainSelectModelSchema
      ),
      documents: many<DocumentDomainQueryModel>(
        DocumentDomainSelectModelSchema
      ),
      files: many<FileDomainQueryModel>(FileDomainSelectModelSchema),
      invoices: many<InvoiceDomainQueryModel>(InvoiceDomainSelectModelSchema),
      owner: one<UserDomainQueryModel>(UserDomainSelectModelSchema),
      package: one<PackageDomainQueryModel>(PackageDomainSelectModelSchema),
      review: one<ReviewDomainQueryModel>(ReviewDomainSelectModelSchema),
      services: many<ProjectServiceDomainQueryModel>(
        ProjectServiceDomainSelectModelSchema
      ),
      tags: many<TagDomainQueryModel>(TagDomainSelectModelSchema),
      thumbnail: one<FileDomainQueryModel>(FileDomainSelectModelSchema),
    })
  )

export type ProjectDomainQueryModel = ProjectViewDomainSelectModel &
  Relations<{
    acceptor: One<UserDomainQueryModel>
    activity: Many<ActivityDomainQueryModel>
    advancePayments: Many<AdvancePaymentDomainQueryModel>
    agencyTeam: One<TeamDomainQueryModel>
    bids: Many<BidDomainQueryModel>
    brandTeam: One<TeamDomainQueryModel>
    deliverables: Many<DeliverableDomainQueryModel>
    documents: Many<DocumentDomainQueryModel>
    files: Many<FileDomainQueryModel>
    invoices: Many<InvoiceDomainQueryModel>
    owner: One<UserDomainQueryModel>
    package: One<PackageDomainQueryModel>
    review: One<ReviewDomainQueryModel>
    services: Many<ProjectServiceDomainQueryModel>
    tags: Many<TagDomainQueryModel>
    thumbnail: One<FileDomainQueryModel>
  }>

/**
 * ProjectServiceDomainQueryModel
 */
export const ProjectServiceDomainQueryModelSchema =
  ProjectServiceDomainSelectModelSchema.extend(
    relations({
      deliverables: many<DeliverableDomainQueryModel>(
        DeliverableDomainSelectModelSchema
      ),
      files: many<FileDomainQueryModel>(FileDomainSelectModelSchema),
      project: one<ProjectDomainQueryModel>(ProjectViewDomainSelectModelSchema),
      review: one<ReviewDomainQueryModel>(ReviewDomainSelectModelSchema),
      tags: many<TagDomainQueryModel>(TagDomainSelectModelSchema),
    })
  )

export type ProjectServiceDomainQueryModel = ProjectServiceDomainSelectModel &
  Relations<{
    deliverables: Many<DeliverableDomainQueryModel>
    files: Many<FileDomainQueryModel>
    project: One<ProjectDomainQueryModel>
    review: One<ReviewDomainQueryModel>
    tags: Many<TagDomainQueryModel>
  }>

/**
 * ProofDomainQueryModel
 */
export const ProofDomainQueryModelSchema = ProofDomainSelectModelSchema.extend(
  relations({
    deliverable: one<DeliverableDomainQueryModel>(
      DeliverableDomainSelectModelSchema
    ),
    file: one<FileDomainQueryModel>(FileDomainSelectModelSchema),
    round: one<RoundDomainQueryModel>(RoundDomainSelectModelSchema),
  })
)

export type ProofDomainQueryModel = SimplifyDeep<
  ProofDomainSelectModel &
    Relations<{
      deliverable: One<DeliverableDomainQueryModel>
      file: One<FileDomainQueryModel>
      round: One<RoundDomainQueryModel>
    }>
>

/**
 * ReviewDomainQueryModel
 */
export const ReviewDomainQueryModelSchema =
  ReviewDomainSelectModelSchema.extend(
    relations({
      project: one<ProjectDomainQueryModel>(ProjectViewDomainSelectModelSchema),
      rounds: many<RoundDomainQueryModel>(RoundDomainSelectModelSchema),
      service: one<ProjectServiceDomainQueryModel>(
        ProjectServiceDomainSelectModelSchema
      ),
    })
  )

export type ReviewDomainQueryModel = ReviewDomainSelectModel &
  Relations<{
    project: One<ProjectDomainQueryModel>
    rounds: Many<RoundDomainQueryModel>
    service: One<ProjectServiceDomainQueryModel>
  }>

/**
 * RoundDomainQueryModel
 */
export const RoundDomainQueryModelSchema = RoundDomainSelectModelSchema.extend(
  relations({
    files: many<FileDomainQueryModel>(FileDomainSelectModelSchema),
    proofs: many<ProofDomainQueryModel>(ProofDomainSelectModelSchema),
    review: one<ReviewDomainQueryModel>(ReviewDomainSelectModelSchema),
  })
).extend({
  proposal: RoundDomainSelectModelSchema.shape.proposal.extend({
    submitted: RoundDomainSelectModelSchema.shape.proposal.shape.submitted
      .unwrap()
      .extend(
        relations({
          actor: one<UserDomainQueryModel>(UserDomainSelectModelSchema),
        })
      )
      .optional(),
  }),
  feedback: RoundDomainSelectModelSchema.shape.feedback.extend({
    submitted: RoundDomainSelectModelSchema.shape.feedback.shape.submitted
      .unwrap()
      .extend(
        relations({
          actor: one<UserDomainQueryModel>(UserDomainSelectModelSchema),
        })
      )
      .optional(),
  }),
})

export type RoundDomainQueryModel = SimplifyDeep<
  OverrideProperties<
    RoundDomainSelectModel &
      Relations<{
        files: Many<FileDomainQueryModel>
        proofs: Many<ProofDomainQueryModel>
        review: One<ReviewDomainQueryModel>
      }>,
    {
      proposal: OverrideProperties<
        RoundDomainSelectModel['proposal'],
        Partial<{
          submitted: RoundDomainSelectModel['proposal']['submitted'] &
            Relations<{
              actor: One<UserDomainQueryModel>
            }>
        }>
      >
      feedback: OverrideProperties<
        RoundDomainSelectModel['feedback'],
        Partial<{
          submitted: RoundDomainSelectModel['feedback']['submitted'] &
            Relations<{
              actor: One<UserDomainQueryModel>
            }>
        }>
      >
    }
  >
>

/**
 * ServiceDomainQueryModel
 */
export const ServiceDomainQueryModelSchema =
  ServiceDomainSelectModelSchema.extend(
    relations({
      tags: many<TagDomainQueryModel>(TagDomainSelectModelSchema),
    })
  )

export type ServiceDomainQueryModel = ServiceDomainSelectModel &
  Relations<{
    tags: Many<TagDomainQueryModel>
  }>

/**
 * SurveyDomainQueryModel
 */
export const SurveyDomainQueryModelSchema =
  SurveyDomainSelectModelSchema.extend(relations({}))

export type SurveyDomainQueryModel = SurveyDomainSelectModel &
  Relations<EmptyObject>

/**
 * TagDomainQueryModel
 */
export const TagDomainQueryModelSchema = TagDomainSelectModelSchema.extend(
  relations({})
)

export type TagDomainQueryModel = TagDomainSelectModel & Relations<EmptyObject>

/**
 * TeamDomainQueryModel
 */
export const TeamDomainQueryModelSchema = TeamDomainSelectModelSchema.extend(
  relations({
    avatar: one<FileDomainQueryModel>(FileDomainSelectModelSchema),
    billingProfile: one<BillingProfileDomainQueryModel>(
      BillingProfileDomainSelectModelSchema
    ),
    files: many<FileDomainQueryModel>(FileDomainSelectModelSchema),
    organization: one<OrganizationDomainQueryModel>(
      OrganizationDomainSelectModelSchema
    ),
    profile: one<TeamProfileDomainQueryModel>(
      TeamProfileDomainSelectModelSchema
    ),
    projects: many<ProjectDomainQueryModel>(ProjectViewDomainSelectModelSchema),
    tags: many<TagDomainQueryModel>(TagDomainSelectModelSchema),
    users: many<UserDomainQueryModel>(UserDomainSelectModelSchema),
  })
)

export type TeamDomainQueryModel = TeamDomainSelectModel &
  Relations<{
    avatar: One<FileDomainQueryModel>
    billingProfile: One<BillingProfileDomainQueryModel>
    files: Many<FileDomainQueryModel>
    organization: One<OrganizationDomainQueryModel>
    profile: One<TeamProfileDomainQueryModel>
    projects: Many<ProjectDomainQueryModel>
    tags: Many<TagDomainQueryModel>
    users: Many<UserDomainQueryModel>
  }>

export const withTeamIds = <TModel extends { teams?: TeamDomainQueryModel[] }>(
  model: TModel
) => ({
  ...model,
  teamIds: model.teams?.map((team) => team.teamId) ?? [],
})

export const hasTeams = <
  TModel extends { teams?: TeamDomainQueryModel[]; teamIds?: TeamId[] },
>({
  teams,
  teamIds,
}: TModel) => (teams && teams.length > 0) || (teamIds && teamIds.length > 0)

export const hasMultipleTeams = <
  TModel extends { teams?: TeamDomainQueryModel[]; teamIds?: TeamId[] },
>({
  teams,
  teamIds,
}: TModel) => (teams && teams.length > 1) || (teamIds && teamIds.length > 1)

export const isMultiTeam = <
  TModel extends {
    teams?: TeamDomainQueryModel[]
    teamIds?: TeamId[]
    organizationType: OrganizationType
  },
>(
  model: TModel
) => hasMultipleTeams(model) || model.organizationType === 'internal'

/**
 * TeamProfileDomainQueryModel
 */
export const TeamProfileDomainQueryModelSchema =
  TeamProfileDomainSelectModelSchema.extend(
    relations({
      team: one<TeamDomainQueryModel>(TeamDomainSelectModelSchema),
    })
  )

export type TeamProfileDomainQueryModel = TeamProfileDomainSelectModel &
  Relations<{
    team: One<TeamDomainQueryModel>
  }>

/**
 * UserDomainQueryModel
 */
export const UserDomainQueryModelSchema = UserDomainSelectModelSchema.extend(
  relations({
    avatar: one<FileDomainQueryModel>(FileDomainSelectModelSchema),
    files: many<FileDomainQueryModel>(FileDomainSelectModelSchema),
    organization: one<OrganizationDomainQueryModel>(
      OrganizationDomainSelectModelSchema
    ),
    tags: many<TagDomainQueryModel>(TagDomainSelectModelSchema),
    teams: many<TeamDomainQueryModel>(TeamDomainSelectModelSchema),
  })
)

export type UserDomainQueryModel = UserDomainSelectModel &
  Relations<{
    avatar: One<FileDomainQueryModel>
    files: Many<FileDomainQueryModel>
    organization: One<OrganizationDomainQueryModel>
    tags: Many<TagDomainQueryModel>
    teams: Many<TeamDomainQueryModel>
  }>

/**
 * NotificationPreferenceDomainQueryModel — union of variants with relations
 */
const NotificationPreferenceQueryRelations = relations({
  user: one<UserDomainQueryModel>(UserDomainSelectModelSchema),
})

export const NotificationPreferenceDomainQueryModelSchema =
  z.discriminatedUnion('activityType', [
    NewProjectNotificationPreferenceSchema.extend(
      NotificationPreferenceQueryRelations
    ),
  ])

export type NotificationPreferenceDomainQueryModel =
  NotificationPreferenceDomainSelectModel &
    Relations<{
      user: One<UserDomainQueryModel>
    }>
